import type {
  AgencyRegister,
  AgencyDocument,
} from "../agency-documents/agencyDocumentsApi";
export interface PortfolioEligibility {
  actionRequired?: boolean;
  actionReason?: string;
  canSubmit: boolean;
  code: string;
  reason: string;
  partnershipId?: number;
}
export interface PortfolioInstitution {
  id: number;
  name: string;
  city?: string | null;
  country?: string | null;
  countries?: { name: string } | null;
  description?: string | null;
  logo?: string | null;
  partnershipId?: number | null;
  partnershipStatus?: string | null;
  state: string;
  eligibility: PortfolioEligibility;
}
export interface AgreementRevision {
  acceptedSigned?: boolean;
  awaitingSignature?: boolean;
  awaitingReview?: boolean;
  id: number;
  revision: string;
  effectiveDate: string;
  expiresOn: string | null;
  resignDeadline: string | null;
  fileName: string;
  mimeType?: string;
  publishedAt?: string;
}
export interface PortfolioList {
  institutions: PortfolioInstitution[];
  summary: { available: number; inProgress: number; activated: number };
  pagination: { page: number; pages: number; total: number };
  capabilities: { canSign: boolean; canRequest: boolean };
}
export interface PortfolioDetail {
  institution: PortfolioInstitution;
  partnership: { id: number; status: string; isActive: boolean } | null;
  eligibility: PortfolioEligibility;
  applicableAgreement: AgreementRevision | null;
  documents: AgencyRegister | null;
  agencyId: number;
  capabilities: { canSign: boolean };
}
export const institutionPortfolioApi = (api: any) => ({
  status: async (
    partnership: number,
    signal: AbortSignal,
  ): Promise<{
    agencyId: number;
    eligibility: PortfolioEligibility;
    agreement: unknown;
    acceptedAgreement?: AgreementRevision | null;
    pendingAgreement?: AgreementRevision | null;
    candidates?: {
      documentId: number;
      versionId: number;
      title: string;
      number: number;
    }[];
  }> =>
    (
      await api.get(
        `/institution-portfolio/admin/partnerships/${partnership}`,
        { signal },
      )
    ).data.data,
  list: async (
    params: Record<string, string | number>,
    signal: AbortSignal,
  ): Promise<PortfolioList> =>
    (await api.get("/institution-portfolio", { params, signal })).data.data,
  detail: async (id: number, signal: AbortSignal): Promise<PortfolioDetail> =>
    (await api.get(`/institution-portfolio/${id}`, { signal })).data.data,
  template: async (id: number, download = false): Promise<Blob> =>
    (
      await api.get(`/institution-portfolio/${id}/agreement/file`, {
        responseType: "blob",
        params: { disposition: download ? "attachment" : "inline" },
      })
    ).data,
  revisions: async (
    university: number,
    partnership: number | undefined,
    signal: AbortSignal,
    page = 1,
  ): Promise<{
    revisions: AgreementRevision[];
    pagination: { page: number; pages: number; total: number };
  }> =>
    (
      await api.get(
        `/institution-portfolio/admin/universities/${university}/revisions`,
        {
          params: {
            page,
            ...(partnership ? { partnershipId: partnership } : {}),
          },
          signal,
        },
      )
    ).data.data,
  publish: async (university: number, body: FormData) =>
    (
      await api.post(
        `/institution-portfolio/admin/universities/${university}/revisions`,
        body,
        { headers: { "Content-Type": "multipart/form-data" } },
      )
    ).data.data,
  designate: async (
    partnership: number,
    documentId: number,
    versionId: number,
  ): Promise<PortfolioEligibility> =>
    (
      await api.post(
        `/institution-portfolio/admin/partnerships/${partnership}/designate`,
        { documentId, versionId },
      )
    ).data.data,
  issue: async (
    partnership: number,
    revisionId: number,
    operationId: string,
  ): Promise<AgencyDocument> =>
    (
      await api.post(
        `/institution-portfolio/admin/partnerships/${partnership}/issue`,
        { revisionId, operationId },
      )
    ).data.data,
});
export type InstitutionPortfolioApi = ReturnType<
  typeof institutionPortfolioApi
>;
